import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import apiClient from '../../services/apiClient';
import AskAiAnswerPanel from './AskAiAnswerPanel';
import ContentCopyGuard from './ContentCopyGuard';

interface TestScoreSummary {
  percent: number;
  point: number;
  maxPoint: number;
  resultId?: number;
  passed?: boolean | null;
}

interface TestViewerProps {
  contentId: number;
  paperId: number;
  courseId: number;
  onComplete: (contentId: number, score?: TestScoreSummary) => void;
  isCompleted: boolean;
  initialHistoryOpen?: boolean;
}

type ViewMode = 'taking' | 'result' | 'history' | 'historyDetail';

type QuizDraft = {
  answers: Record<number, any>;
  flaggedIds: Record<number, boolean>;
  startedAt: number | null;
  currentIndex: number;
};

const formatDuration = (totalSeconds: number | null | undefined) => {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return '—';
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
};

const isEmptyHtml = (value: any) => {
  if (value === undefined || value === null || value === '') return true;
  if (typeof value !== 'string') return false;
  const stripped = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return stripped === '';
};

const hasAnswerValue = (value: any) => {
  if (Array.isArray(value)) return value.length > 0;
  return !isEmptyHtml(value);
};

const essayEditorFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'image',
  'code-block',
];

const draftStorageKey = (courseId: number, contentId: number, paperId: number) =>
  `quiz-draft:${courseId}:${contentId}:${paperId}`;

const loadQuizDraft = (key: string): QuizDraft | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      answers: parsed?.answers && typeof parsed.answers === 'object' ? parsed.answers : {},
      flaggedIds: parsed?.flaggedIds && typeof parsed.flaggedIds === 'object' ? parsed.flaggedIds : {},
      startedAt: typeof parsed?.startedAt === 'number' ? parsed.startedAt : null,
      currentIndex: typeof parsed?.currentIndex === 'number' ? parsed.currentIndex : 0,
    };
  } catch {
    return null;
  }
};

const saveQuizDraft = (key: string, draft: QuizDraft) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // ignore quota / private mode
  }
};

const clearQuizDraft = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const uploadImageFile = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/student/uploads/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  if (!response.data?.success || !response.data?.data?.url) {
    throw new Error(response.data?.message || 'Upload ảnh thất bại');
  }
  return response.data.data.url as string;
};

/** Replace base64 <img src="data:..."> with GCS URLs */
const replaceBase64ImagesInHtml = async (html: string): Promise<string> => {
  if (!html || !html.includes('data:image')) return html;
  const regex = /src=(["'])(data:image\/[a-zA-Z0-9+.-]+;base64,[^"']+)\1/gi;
  const matches = [...html.matchAll(regex)];
  if (!matches.length) return html;

  let next = html;
  for (let i = 0; i < matches.length; i += 1) {
    const dataUrl = matches[i][2];
    try {
      const file = await dataUrlToFile(dataUrl, `paste-${Date.now()}-${i}.png`);
      const url = await uploadImageFile(file);
      next = next.split(dataUrl).join(url);
    } catch (err) {
      console.error('Failed to upload pasted image', err);
    }
  }
  return next;
};

const HtmlContent: React.FC<{ html?: string | null; className?: string }> = ({ html, className }) => {
  if (!html) return null;
  return (
    <div
      className={`prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const TestViewer: React.FC<TestViewerProps> = ({
  contentId,
  paperId,
  courseId,
  onComplete,
  isCompleted: _isCompleted,
  initialHistoryOpen = false,
}) => {
  const draftKey = useMemo(
    () => draftStorageKey(courseId, contentId, paperId),
    [courseId, contentId, paperId]
  );

  const [testData, setTestData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [flaggedIds, setFlaggedIds] = useState<Record<number, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(initialHistoryOpen ? 'history' : 'taking');
  const [resultDetail, setResultDetail] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [canRetake, setCanRetake] = useState(true);
  const [, setHasEssay] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [askAiOpen, setAskAiOpen] = useState(false);
  const autoSubmittedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const currentQuestionIdRef = useRef<number | null>(null);
  const base64UploadTimerRef = useRef<number | null>(null);
  const skipDraftPersistRef = useRef(false);

  const essayModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link', 'image', 'code-block'],
          ['clean'],
        ],
        handlers: {
          image() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();
            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;
              const quill = (this as any).quill;
              const range = quill?.getSelection(true);
              const insertAt = range?.index ?? quill?.getLength?.() ?? 0;
              try {
                setUploadingImage(true);
                const url = await uploadImageFile(file);
                quill.insertEmbed(insertAt, 'image', url, 'user');
                quill.setSelection(insertAt + 1, 0, 'silent');
              } catch (err: any) {
                console.error(err);
                alert(err?.response?.data?.message || err?.message || 'Upload ảnh thất bại');
              } finally {
                setUploadingImage(false);
              }
            };
          },
        },
      },
    }),
    []
  );

  const fetchTestDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/student/tests/${paperId}`);
      if (response.data.success) {
        const data = response.data.data;
        setTestData(data);
        setCanRetake(typeof data?.canRetake === 'boolean' ? data.canRetake : true);
        if (typeof data?.hasEssay === 'boolean') setHasEssay(data.hasEssay);
        const attemptCount = Number(data?.attemptCount ?? 0);
        if (
          !initialHistoryOpen &&
          data?.canRetake === false &&
          attemptCount > 0
        ) {
          setViewMode((prev) => (prev === 'taking' ? 'history' : prev));
        }
      }
    } catch (error) {
      console.error('Failed to fetch test details', error);
    } finally {
      setLoading(false);
    }
  }, [paperId, initialHistoryOpen]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await apiClient.get(`/student/tests/${paperId}/history`, {
        params: { courseId },
      });
      if (response.data.success) {
        setHistory(response.data.data || []);
        if (typeof response.data.canRetake === 'boolean') setCanRetake(response.data.canRetake);
        if (typeof response.data.hasEssay === 'boolean') setHasEssay(response.data.hasEssay);
      }
    } catch (error) {
      console.error('Failed to fetch test history', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [paperId, courseId]);

  useEffect(() => {
    skipDraftPersistRef.current = true;
    const draft = loadQuizDraft(draftKey);
    setAnswers(draft?.answers || {});
    setFlaggedIds(draft?.flaggedIds || {});
    setResultDetail(null);
    setCurrentIndex(draft?.currentIndex || 0);
    setViewMode(initialHistoryOpen ? 'history' : 'taking');
    setCanRetake(true);
    setHasEssay(false);
    autoSubmittedRef.current = false;
    setStartedAt(draft?.startedAt || Date.now());
    void fetchTestDetails();
    void fetchHistory();
    window.setTimeout(() => {
      skipDraftPersistRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only rehydrate when quiz identity changes
  }, [paperId, courseId, contentId, draftKey, fetchTestDetails, fetchHistory]);

  useEffect(() => {
    if (initialHistoryOpen) {
      setViewMode('history');
      void fetchHistory();
    }
  }, [initialHistoryOpen, fetchHistory]);

  useEffect(() => {
    if (skipDraftPersistRef.current || viewMode !== 'taking') return;
    saveQuizDraft(draftKey, {
      answers,
      flaggedIds,
      startedAt,
      currentIndex,
    });
  }, [answers, flaggedIds, startedAt, currentIndex, viewMode, draftKey]);

  useEffect(() => {
    if (!testData?.minute || viewMode !== 'taking' || !startedAt) {
      setRemainingSeconds(null);
      return;
    }
    const total = Number(testData.minute) * 60;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setRemainingSeconds(Math.max(0, total - elapsed));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [testData?.minute, viewMode, startedAt]);

  const takingQuestions = useMemo(() => {
    const list = (testData?.questions || []).slice();
    list.sort((a: any, b: any) => (a.sort ?? 0) - (b.sort ?? 0));
    return list.map((pq: any, index: number) => ({
      index,
      paperQuestion: pq,
      questionId: pq.question_id || pq.questionData?.id,
      questionData: pq.questionData,
      groupId: pq.group_id,
      maxPoint: pq.point,
    }));
  }, [testData]);

  const resultQuestions = useMemo(() => {
    return (resultDetail?.questions || []).map((item: any, index: number) => ({
      ...item,
      index,
    }));
  }, [resultDetail]);

  const groupMap = useMemo(() => {
    const map = new Map<number, any>();
    (testData?.groups || resultDetail?.groups || []).forEach((g: any) => map.set(g.id, g));
    return map;
  }, [testData, resultDetail]);

  const activeList = viewMode === 'taking' ? takingQuestions : resultQuestions;
  const safeIndex = Math.min(currentIndex, Math.max(activeList.length - 1, 0));
  const currentItem = activeList[safeIndex];

  useEffect(() => {
    if (safeIndex !== currentIndex) setCurrentIndex(safeIndex);
  }, [safeIndex, currentIndex]);

  useEffect(() => {
    currentQuestionIdRef.current = currentItem?.questionId ?? null;
  }, [currentItem?.questionId]);

  const handleSubmit = useCallback(async (force = false) => {
    if (submitting || viewMode !== 'taking') return;
    if (!force) {
      setConfirmSubmitOpen(true);
      return;
    }

    setConfirmSubmitOpen(false);
    setSubmitting(true);
    try {
      const testedSecond = startedAt
        ? Math.max(1, Math.floor((Date.now() - startedAt) / 1000))
        : undefined;
      const response = await apiClient.post(`/student/tests/${paperId}/submit`, {
        courseId,
        courseContentId: contentId,
        answers: answersRef.current,
        testedSecond,
      });

      if (response.data.success) {
        clearQuizDraft(draftKey);
        const detail = response.data.data;
        setResultDetail(detail);
        if (typeof detail?.canRetake === 'boolean') setCanRetake(detail.canRetake);
        else if (typeof response.data.canRetake === 'boolean') setCanRetake(response.data.canRetake);
        if (typeof detail?.hasEssay === 'boolean') setHasEssay(detail.hasEssay);
        else if (typeof response.data.hasEssay === 'boolean') setHasEssay(response.data.hasEssay);
        setCurrentIndex(0);
        setViewMode('result');
        onComplete(contentId, {
          percent: detail?.percent ?? 0,
          point: detail?.point ?? 0,
          maxPoint: detail?.maxPoint ?? 0,
          resultId: detail?.id,
        });
        void fetchHistory();
        toast.success(response.data.message || 'Nộp bài thành công');
      } else {
        toast.error(response.data.message || 'Nộp bài thất bại');
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi nộp bài');
    } finally {
      setSubmitting(false);
    }
  }, [submitting, viewMode, startedAt, paperId, courseId, contentId, onComplete, fetchHistory, draftKey]);

  useEffect(() => {
    if (
      viewMode === 'taking' &&
      remainingSeconds === 0 &&
      testData?.minute > 0 &&
      !autoSubmittedRef.current &&
      !submitting
    ) {
      autoSubmittedRef.current = true;
      void handleSubmit(true);
    }
  }, [remainingSeconds, viewMode, testData?.minute, submitting, handleSubmit]);

  const openHistory = async () => {
    setViewMode('history');
    await fetchHistory();
  };

  const openHistoryDetail = async (resultId: number) => {
    try {
      const response = await apiClient.get(`/student/test-results/${resultId}`);
      if (response.data.success) {
        const detail = response.data.data;
        setResultDetail(detail);
        if (typeof detail?.canRetake === 'boolean') setCanRetake(detail.canRetake);
        if (typeof detail?.hasEssay === 'boolean') setHasEssay(detail.hasEssay);
        setCurrentIndex(0);
        setViewMode('historyDetail');
      }
    } catch (error) {
      console.error(error);
      alert('Không tải được chi tiết lần làm bài');
    }
  };

  const startRetake = () => {
    if (!canRetake) return;
    const draft = loadQuizDraft(draftKey);
    setAnswers(draft?.answers || {});
    setFlaggedIds(draft?.flaggedIds || {});
    setResultDetail(null);
    setCurrentIndex(draft?.currentIndex || 0);
    autoSubmittedRef.current = false;
    setStartedAt(draft?.startedAt || Date.now());
    setViewMode('taking');
  };

  const handleAnswerChange = useCallback((questionId: number, value: any) => {
    setAnswers((prev) => {
      if (prev[questionId] === value) return prev;
      return { ...prev, [questionId]: value };
    });
  }, []);

  const handleMultiChoiceToggle = useCallback((questionId: number, answerId: number) => {
    setAnswers((prev) => {
      const current: number[] = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const exists = current.includes(answerId);
      const next = exists ? current.filter((id) => id !== answerId) : [...current, answerId];
      return { ...prev, [questionId]: next };
    });
  }, []);

  const handleEssayChange = useCallback(
    (questionId: number, html: string, source?: string) => {
      if (source === 'silent') return;
      // Quill often emits empty HTML on unmount when switching questions — don't wipe cache
      if (isEmptyHtml(html) && hasAnswerValue(answersRef.current[questionId])) {
        return;
      }
      if (currentQuestionIdRef.current != null && currentQuestionIdRef.current !== questionId) {
        return;
      }
      handleAnswerChange(questionId, html);

      if (html?.includes('data:image')) {
        if (base64UploadTimerRef.current) {
          window.clearTimeout(base64UploadTimerRef.current);
        }
        base64UploadTimerRef.current = window.setTimeout(async () => {
          try {
            setUploadingImage(true);
            const replaced = await replaceBase64ImagesInHtml(html);
            if (replaced !== html && currentQuestionIdRef.current === questionId) {
              handleAnswerChange(questionId, replaced);
            }
          } finally {
            setUploadingImage(false);
          }
        }, 400);
      }
    },
    [handleAnswerChange]
  );

  const toggleFlag = (questionId: number) => {
    setFlaggedIds((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const goTo = (index: number) => {
    if (index < 0 || index >= activeList.length) return;
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <div className="text-center py-10 bg-m2-surface-container rounded-xl h-full flex flex-col items-center justify-center">
        <i className="fas fa-spinner fa-spin text-2xl text-m2-primary"></i>
        <p className="mt-2 text-m2-on-surface-variant">Đang tải bài thi...</p>
      </div>
    );
  }

  if (!testData) {
    return (
      <div className="text-center py-10 text-red-500 bg-m2-surface-container rounded-xl h-full flex items-center justify-center">
        Không tìm thấy thông tin bài thi.
      </div>
    );
  }

  const timerUrgent = remainingSeconds != null && remainingSeconds <= 60;
  const answeredCount = takingQuestions.filter((q: any) => hasAnswerValue(answers[q.questionId])).length;
  const isReviewMode = viewMode === 'result' || viewMode === 'historyDetail';

  const getNavStatus = (item: any) => {
    if (isReviewMode) {
      if (item.status === 'CORRECT') return 'correct';
      if (item.status === 'WRONG') return 'wrong';
      if (item.status === 'PENDING') return 'pending';
      return 'unanswered';
    }
    const qid = item.questionId;
    const answered = hasAnswerValue(answers[qid]);
    const flagged = !!flaggedIds[qid];
    if (answered && flagged) return 'answered-flagged';
    if (flagged) return 'flagged';
    if (answered) return 'answered';
    return 'unanswered';
  };

  const navClass = (status: string, isActive: boolean) => {
    const base = 'relative w-8 h-8 rounded-md text-[11px] font-bold border transition flex items-center justify-center';
    const activeRing = isActive ? ' ring-2 ring-offset-1 ring-blue-500' : '';
    switch (status) {
      case 'correct':
        return `${base}${activeRing} bg-green-500 text-white border-green-600`;
      case 'wrong':
        return `${base}${activeRing} bg-red-500 text-white border-red-600`;
      case 'pending':
        return `${base}${activeRing} bg-amber-400 text-white border-amber-500`;
      case 'flagged':
        return `${base}${activeRing} bg-amber-50 text-amber-800 border-amber-400`;
      case 'answered-flagged':
        return `${base}${activeRing} bg-m2-primary text-m2-on-primary border-amber-400`;
      case 'answered':
        return `${base}${activeRing} bg-m2-primary text-m2-on-primary border-blue-700`;
      default:
        return `${base}${activeRing} bg-m2-surface-container text-m2-on-surface-variant border-m2-outline hover:border-m2-primary/40`;
    }
  };

  const renderNavSideButtons = () => (
    <>
      <button
        type="button"
        onClick={() => goTo(safeIndex - 1)}
        disabled={safeIndex <= 0}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-m2-surface-container/95 border border-m2-outline-variant shadow-md text-m2-on-surface hover:bg-m2-primary/10 hover:text-m2-primary disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        title="Câu trước"
      >
        <i className="fas fa-chevron-left text-sm"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(safeIndex + 1)}
        disabled={safeIndex >= activeList.length - 1}
        className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-m2-surface-container/95 border border-m2-outline-variant shadow-md text-m2-on-surface hover:bg-m2-primary/10 hover:text-m2-primary disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        title="Câu sau"
      >
        <i className="fas fa-chevron-right text-sm"></i>
      </button>
    </>
  );

  const renderTakingQuestion = () => {
    if (!currentItem?.questionData) return null;
    const qData = currentItem.questionData;
    const qId = currentItem.questionId;
    const qType = String(qData.question_type || '').toUpperCase();
    const hasChoices = Array.isArray(qData.answers) && qData.answers.length > 0;
    const group = currentItem.groupId ? groupMap.get(currentItem.groupId) : null;
    const isFlagged = !!flaggedIds[qId];

    return (
      <div className="relative h-full min-h-0">
        {renderNavSideButtons()}
        <div className="h-full overflow-y-auto custom-scrollbar px-12 space-y-3">
          <ContentCopyGuard>
            {group && (
              <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-purple-950">
                <p className="text-xs font-bold uppercase tracking-wide text-purple-800 mb-1">Tình huống</p>
                {/* DB stores scenario HTML in `name` (no description column) */}
                <HtmlContent
                  html={group.description || group.name}
                  className="text-purple-950 prose-p:text-purple-950 prose-strong:text-purple-950 prose-li:text-purple-950 prose-img:max-w-full prose-img:rounded-lg prose-pre:whitespace-pre-wrap"
                />
              </div>
            )}

            <div className="rounded-xl border border-m2-outline-variant bg-m2-surface-low p-4 md:p-5 mt-3">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-flex items-center rounded-md m2-cta-gradient px-2.5 py-1 text-xs font-bold text-white">
                      Câu {safeIndex + 1}/{activeList.length}
                    </span>
                    {currentItem.maxPoint != null && (
                      <span className="text-[11px] font-semibold text-m2-primary">{currentItem.maxPoint} điểm</span>
                    )}
                  </div>
                  <HtmlContent html={qData.content} className="font-semibold text-m2-on-surface prose-p:font-semibold prose-p:my-1" />
                </div>
                <button
                  type="button"
                  onClick={() => toggleFlag(qId)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
                    isFlagged
                      ? 'bg-amber-100 border-amber-300 text-amber-800'
                      : 'bg-m2-surface-container border-m2-outline-variant text-m2-on-surface-variant hover:bg-m2-surface-low'
                  }`}
                  title="Đánh dấu chưa chắc chắn"
                >
                  <i className={`fas fa-flag ${isFlagged ? 'text-amber-600' : 'text-m2-outline'}`}></i>
                  {isFlagged ? 'Đã đánh dấu' : 'Chưa chắc'}
                </button>
              </div>

              {(qType === 'CHOICE' || qType === 'MULTIPLE_CHOICE' || qType === 'SINGLE_CHOICE' || hasChoices) && (() => {
                const isMulti = qType === 'MULTIPLE_CHOICE';
                const selectedIds: number[] = isMulti
                  ? (Array.isArray(answers[qId]) ? answers[qId] : [])
                  : [];
                return (
                  <div className="space-y-2">
                    {isMulti && (
                      <p className="text-xs text-m2-on-surface-variant italic mb-1">
                        <i className="fas fa-check-square mr-1"></i>
                        Câu hỏi nhiều đáp án — có thể chọn nhiều lựa chọn
                      </p>
                    )}
                    {qData.answers.map((ans: any) => {
                      const selected = isMulti
                        ? selectedIds.includes(ans.id)
                        : answers[qId] === ans.id;
                      return (
                        <label
                          key={ans.id}
                          className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition ${
                            selected
                              ? 'bg-m2-primary/10 border-m2-primary/40'
                              : 'bg-m2-surface-container border-m2-outline-variant hover:border-m2-primary/30'
                          }`}
                        >
                          {isMulti ? (
                            <input
                              type="checkbox"
                              className="mt-1 w-4 h-4 rounded text-m2-primary focus:ring-m2-primary border-m2-outline"
                              checked={selected}
                              onChange={() => handleMultiChoiceToggle(qId, ans.id)}
                            />
                          ) : (
                            <input
                              type="radio"
                              name={`q-${qId}`}
                              className="mt-1 w-4 h-4 text-m2-primary focus:ring-m2-primary border-m2-outline"
                              checked={selected}
                              onChange={() => handleAnswerChange(qId, ans.id)}
                            />
                          )}
                          <HtmlContent html={ans.content} className="text-m2-on-surface" />
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </ContentCopyGuard>

          {qType === 'ESSAY' && (
            <div className="essay-editor rounded-lg border border-m2-outline bg-m2-surface-container overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-m2-primary relative">
              {uploadingImage && (
                <div className="absolute inset-x-0 top-0 z-10 bg-m2-primary/10/95 border-b border-m2-primary/20 px-3 py-1.5 text-xs font-semibold text-m2-primary flex items-center gap-2">
                  <i className="fas fa-spinner fa-spin"></i>
                  Đang upload ảnh lên Google Storage...
                </div>
              )}
              <ReactQuill
                key={`essay-editor-${qId}`}
                theme="snow"
                value={answers[qId] || ''}
                onChange={(html, _delta, source) => handleEssayChange(qId, html, source)}
                modules={essayModules}
                formats={essayEditorFormats}
                placeholder="Nhập câu trả lời tự luận của bạn..."
                className="essay-quill bg-m2-surface-container"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReviewQuestion = () => {
    const item = currentItem;
    if (!item?.questionData) return null;
    const qData = item.questionData;
    const qType = String(qData.question_type || '').toUpperCase();
    const selectedSet = new Set(item.selectedAnswerIds || []);
    const group = item.groupId ? groupMap.get(item.groupId) : null;
    const statusBadge =
      item.status === 'CORRECT'
        ? { text: 'Đúng', cls: 'bg-green-600 text-white border-green-700' }
        : item.status === 'WRONG'
          ? { text: 'Sai', cls: 'bg-red-600 text-white border-red-700' }
          : item.status === 'PENDING'
            ? { text: 'Chờ chấm', cls: 'bg-amber-500 text-white border-amber-600' }
            : { text: 'Chưa trả lời', cls: 'bg-m2-surface-high text-m2-on-surface-variant border-m2-outline-variant' };

    const canAskAi = Boolean(resultDetail?.id && item.questionId);

    return (
      <div className="relative h-full min-h-0">
        {renderNavSideButtons()}
        <ContentCopyGuard
          className="h-full overflow-y-auto custom-scrollbar px-12 space-y-3"
          onAskAi={() => {
            if (canAskAi) setAskAiOpen(true);
          }}
          askAiDisabled={!canAskAi}
          askAiDisabledReason="Chưa có kết quả bài làm để hỏi AI"
        >
          {group && (
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-purple-950">
              <p className="text-xs font-bold uppercase tracking-wide text-purple-800 mb-1">Tình huống</p>
              {/* DB stores scenario HTML in `name` (no description column) */}
              <HtmlContent
                html={group.description || group.name}
                className="text-purple-950 prose-p:text-purple-950 prose-strong:text-purple-950 prose-li:text-purple-950 prose-img:max-w-full prose-img:rounded-lg prose-pre:whitespace-pre-wrap"
              />
            </div>
          )}

          <div
            className={`rounded-xl border p-4 md:p-5 ${
              item.status === 'CORRECT'
                ? 'bg-green-50/40 border-green-200'
                : item.status === 'WRONG'
                  ? 'bg-m2-error/10/40 border-m2-error/30'
                  : 'bg-m2-surface-low border-m2-outline-variant'
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <div className="mb-2">
                  <span className="inline-flex items-center rounded-md m2-cta-gradient px-2.5 py-1 text-xs font-bold text-white">
                    Câu {safeIndex + 1}/{activeList.length}
                  </span>
                </div>
                <HtmlContent html={qData.content} className="font-semibold text-m2-on-surface prose-p:font-semibold prose-p:my-1" />
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${statusBadge.cls}`}>
                  {statusBadge.text}
                  {item.maxPoint != null ? ` · ${item.point ?? 0}/${item.maxPoint}` : ''}
                </span>
                {canAskAi && (
                  <button
                    type="button"
                    onClick={() => setAskAiOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-m2-primary/30 bg-m2-primary/10 px-2.5 py-1.5 text-xs font-semibold text-m2-primary hover:bg-m2-primary/15"
                    title="Hỏi AI giải thích câu này (hoặc chuột phải)"
                  >
                    <i className="fas fa-robot"></i>
                    Ask AI
                  </button>
                )}
              </div>
            </div>

            {Array.isArray(qData.answers) && qData.answers.length > 0 && (
              <div className="space-y-2">
                {qData.answers.map((ans: any) => {
                  const isSelected = selectedSet.has(ans.id);
                  const isCorrect = ans.isCorrect === true;
                  let rowCls = 'border-m2-outline-variant bg-m2-surface-container text-m2-on-surface';
                  let contentCls = 'text-m2-on-surface';
                  if (isCorrect) {
                    // Light bg needs dark text (dark theme on-surface is too light to read)
                    rowCls = 'border-green-500 bg-green-100 text-green-950';
                    contentCls = 'text-green-950 prose-p:text-green-950 prose-strong:text-green-950 prose-li:text-green-950';
                  } else if (isSelected && !isCorrect) {
                    rowCls = 'border-red-400 bg-red-100 text-red-950';
                    contentCls = 'text-red-950 prose-p:text-red-950 prose-strong:text-red-950 prose-li:text-red-950';
                  }

                  return (
                    <div key={ans.id} className={`flex items-start gap-3 p-3 rounded-lg border ${rowCls}`}>
                      <div className="pt-0.5 w-5 text-center shrink-0">
                        {isCorrect ? (
                          <i className="fas fa-check-circle text-green-700"></i>
                        ) : isSelected ? (
                          <i className="fas fa-times-circle text-red-600"></i>
                        ) : (
                          <i className="far fa-circle text-m2-outline-variant"></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <HtmlContent html={ans.content} className={contentCls} />
                        <div className="text-[11px] mt-1 space-x-2">
                          {isSelected && (
                            <span className={`font-medium ${isCorrect ? 'text-green-800' : 'text-red-700'}`}>
                              Bạn chọn
                            </span>
                          )}
                          {isCorrect && <span className="text-green-800 font-semibold">Đáp án đúng</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {(qType === 'ESSAY' || item.answerContent) && (
              <div className="mt-3 bg-m2-surface-container border border-m2-outline-variant rounded-lg p-3">
                <p className="text-xs font-semibold text-m2-on-surface-variant mb-1">Câu trả lời của bạn</p>
                {hasAnswerValue(item.answerContent) ? (
                  <HtmlContent
                    html={item.answerContent}
                    className="text-sm text-m2-on-surface prose-img:max-w-full prose-pre:whitespace-pre-wrap"
                  />
                ) : (
                  <p className="text-sm italic text-m2-outline">Không có nội dung</p>
                )}
              </div>
            )}

            {qType === 'ESSAY' && item.mentorComment && (
              <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3 text-violet-950">
                <p className="text-xs font-bold uppercase tracking-wide text-violet-800 mb-2 flex items-center gap-2">
                  <i className="fas fa-chalkboard-teacher"></i>
                  Nhận xét mentor
                </p>
                <HtmlContent
                  html={item.mentorComment}
                  className="text-violet-950 prose-p:text-violet-950 prose-strong:text-violet-950 prose-li:text-violet-950 prose-img:max-w-full prose-pre:whitespace-pre-wrap"
                />
              </div>
            )}

            {qData.helpContent && (
              <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sky-950">
                <p className="text-xs font-bold uppercase tracking-wide text-sky-800 mb-2 flex items-center gap-2">
                  <i className="fas fa-lightbulb"></i>
                  Giải thích đáp án
                </p>
                <HtmlContent
                  html={qData.helpContent}
                  className="text-sky-950 prose-p:text-sky-950 prose-strong:text-sky-950 prose-li:text-sky-950"
                />
              </div>
            )}

            {qType === 'ESSAY' && qData.essayAnswer && (
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-950">
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-800 mb-2">Gợi ý / đáp án mẫu</p>
                <HtmlContent
                  html={qData.essayAnswer}
                  className="text-emerald-950 prose-p:text-emerald-950 prose-strong:text-emerald-950 prose-li:text-emerald-950"
                />
              </div>
            )}
          </div>
        </ContentCopyGuard>
      </div>
    );
  };

  const renderNavigator = () => {
    const list = isReviewMode ? resultQuestions : takingQuestions;
    if (!list.length) return null;

    return (
      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-2.5 py-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5 text-[10px] text-m2-on-surface-variant">
          {viewMode === 'taking' ? (
            <>
              <span className="font-semibold text-m2-on-surface uppercase tracking-wide">Câu hỏi</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-m2-surface-container border border-m2-outline"></span>Chưa</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-m2-primary"></span>Đã ({answeredCount}/{list.length})</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-100 border border-amber-400"></span>Chưa chắc</span>
            </>
          ) : (
            <>
              <span className="font-semibold text-m2-on-surface uppercase tracking-wide">Kết quả</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-green-500"></span>Đúng</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500"></span>Sai</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-400"></span>Chờ chấm</span>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {list.map((item: any, idx: number) => {
            const status = getNavStatus(item);
            return (
              <button
                key={item.questionId || idx}
                type="button"
                onClick={() => goTo(idx)}
                className={navClass(status, idx === safeIndex)}
                title={
                  status === 'correct'
                    ? 'Đúng'
                    : status === 'wrong'
                      ? 'Sai'
                      : status === 'flagged'
                        ? 'Đánh dấu chưa chắc'
                        : status === 'answered-flagged'
                          ? 'Đã làm · chưa chắc'
                          : status === 'answered'
                            ? 'Đã làm'
                            : status === 'pending'
                              ? 'Chờ chấm'
                              : 'Chưa làm'
                }
              >
                {idx + 1}
                {(status === 'flagged' || status === 'answered-flagged') && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 border border-white"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-m2-surface-container rounded-xl shadow-sm border border-m2-outline-variant p-2 md:p-3 w-full h-full flex flex-col min-h-0">
      <div className="border-b border-m2-outline-variant pb-2 mb-2 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-base md:text-lg font-black text-m2-on-surface uppercase tracking-tight line-clamp-1">
              {testData.name}
            </h2>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[11px] font-medium text-m2-on-surface-variant">
              <span><i className="far fa-clock mr-1 text-m2-primary"></i>{testData.minute} phút</span>
              <span><i className="far fa-star mr-1 text-yellow-500"></i>{testData.max_point} điểm</span>
              <span><i className="fas fa-list-ol mr-1 text-m2-outline"></i>{testData.total_question || takingQuestions.length} câu</span>
              {isReviewMode && resultDetail && (
                <span className="text-m2-primary font-bold">
                  {resultDetail.point}/{resultDetail.maxPoint} ({resultDetail.percent}%)
                </span>
              )}
              {!canRetake && (
                <span className="text-amber-700 font-semibold">
                  <i className="fas fa-lock mr-1"></i>
                  Chỉ được làm 1 lần (có câu tự luận)
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {viewMode === 'taking' && remainingSeconds != null && (
              <div
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-sm font-bold border ${
                  timerUrgent
                    ? 'bg-m2-error/10 text-red-700 border-m2-error/30 animate-pulse'
                    : 'bg-slate-900 text-white border-slate-700'
                }`}
              >
                <i className="fas fa-hourglass-half text-xs"></i>
                {formatDuration(remainingSeconds)}
              </div>
            )}
            <button
              type="button"
              onClick={() => void openHistory()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-m2-outline-variant bg-m2-surface-container px-2.5 py-1.5 text-xs font-semibold text-m2-on-surface hover:bg-m2-surface-low"
              title="Lịch sử làm bài"
            >
              <i className="fas fa-history text-m2-primary"></i>
              {history.length > 0 && (
                <span className="rounded-full bg-m2-primary/15 text-m2-primary text-[10px] px-1.5">{history.length}</span>
              )}
            </button>
            {viewMode === 'taking' && (
              <button
                onClick={() => void handleSubmit(false)}
                disabled={submitting}
                className="m2-cta-gradient hover:opacity-90 text-white font-bold py-1.5 px-4 rounded-lg text-sm shadow-sm transition-all flex items-center disabled:opacity-60"
              >
                {submitting ? <i className="fas fa-spinner fa-spin mr-1.5"></i> : <i className="fas fa-paper-plane mr-1.5"></i>}
                Nộp bài
              </button>
            )}
            {isReviewMode && canRetake && (
              <button
                type="button"
                onClick={startRetake}
                className="m2-cta-gradient hover:opacity-90 text-white font-bold py-1.5 px-4 rounded-lg text-sm"
              >
                <i className="fas fa-redo mr-1.5"></i>
                Làm lại
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-grow min-h-0 overflow-hidden">
        {viewMode === 'history' && (
          <div className="h-full overflow-y-auto custom-scrollbar pr-1">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-m2-on-surface">Lịch sử làm bài</h3>
                {!canRetake && (
                  <p className="text-xs text-amber-700 mt-0.5">
                    Bài có tự luận chỉ được làm 1 lần
                  </p>
                )}
              </div>
              {canRetake ? (
                <button type="button" onClick={startRetake} className="text-sm font-semibold text-m2-primary hover:text-m2-primary shrink-0">
                  ← Quay lại làm bài
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (history.length > 0) {
                      void openHistoryDetail(history[0].id);
                    }
                  }}
                  className="text-sm font-semibold text-m2-on-surface-variant hover:text-m2-on-surface shrink-0"
                  disabled={history.length === 0}
                >
                  ← Đóng lịch sử
                </button>
              )}
            </div>
            {loadingHistory ? (
              <div className="text-center py-10 text-m2-on-surface-variant">
                <i className="fas fa-spinner fa-spin mr-2"></i>Đang tải lịch sử...
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-10 text-m2-on-surface-variant border border-dashed border-m2-outline-variant rounded-xl">
                Chưa có lần làm bài nào.
              </div>
            ) : (
              <div className="space-y-3 max-w-3xl">
                {history.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => void openHistoryDetail(item.id)}
                    className="w-full text-left rounded-xl border border-m2-outline-variant bg-m2-surface-container p-4 hover:border-m2-primary/40 hover:bg-m2-primary/10/40 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-bold text-m2-on-surface">
                          Lần {item.attemptNo}
                          {item.isHighestPoint && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                              Điểm cao nhất
                            </span>
                          )}
                          {item.isLastTested && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide bg-m2-primary/15 text-m2-primary px-2 py-0.5 rounded-full">
                              Mới nhất
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-m2-on-surface-variant mt-1">
                          {item.savedAt ? new Date(item.savedAt).toLocaleString('vi-VN') : '—'}
                          {' · '}Thời gian làm: {formatDuration(item.testedSecond)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black text-m2-primary">{item.point}/{item.maxPoint}</p>
                        <p className="text-xs text-m2-on-surface-variant">{item.percent}%</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isReviewMode && resultDetail && (
          <div className="h-full flex flex-col min-h-0">
            {renderReviewQuestion()}
          </div>
        )}

        {viewMode === 'taking' && (
          <div className="h-full min-h-0">
            {renderTakingQuestion()}
          </div>
        )}
      </div>

      {(viewMode === 'taking' || isReviewMode) && activeList.length > 0 && (
        <div className="pt-2 mt-2 border-t border-m2-outline-variant shrink-0">
          {renderNavigator()}
        </div>
      )}

      <Transition appear show={confirmSubmitOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[90]" onClose={() => !submitting && setConfirmSubmitOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-m2-surface-container border border-m2-outline-variant p-6 text-left shadow-xl transition-all">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-400">
                      <i className="fas fa-paper-plane" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <Dialog.Title className="text-lg font-bold text-m2-on-surface">
                        Xác nhận nộp bài
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-m2-on-surface-variant leading-relaxed">
                        Bạn có chắc muốn nộp bài? Sau khi nộp sẽ không thể chỉnh sửa câu trả lời của lần làm này.
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => setConfirmSubmitOpen(false)}
                      className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-4 py-2 text-sm font-semibold text-m2-on-surface hover:bg-m2-surface-high disabled:opacity-50"
                    >
                      Tiếp tục làm
                    </button>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => void handleSubmit(true)}
                      className="m2-cta-gradient rounded-lg px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      {submitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                      Nộp bài ngay
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {askAiOpen && resultDetail?.id && currentItem?.questionId && (
        <AskAiAnswerPanel
          open={askAiOpen}
          onClose={() => setAskAiOpen(false)}
          resultId={Number(resultDetail.id)}
          questionId={Number(currentItem.questionId)}
          paperId={paperId}
        />
      )}
    </div>
  );
};

export default TestViewer;
